import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Camera, Video, Heart, Clock, Plane, Globe, Sparkles, Smile } from 'lucide-react';
import Header from '../components/Header';
import { Reveal, Stagger, StaggerItem } from '../components/motion/Reveal';
import { Magnetic } from '../components/motion/Magnetic';
import SEOHead, { breadcrumbSchema } from '../components/SEOHead';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Icon mapping
const iconMap = {
  camera: Camera,
  video: Video,
  heart: Heart,
  clock: Clock,
  plane: Plane,
  globe: Globe,
  sparkles: Sparkles,
  smile: Smile
};

// Helper to validate logo URLs
const isValidLogoUrl = (url) => {
  if (!url) return false;
  return url.includes('cloudfront.net') || 
         url.includes('amazonaws.com') || 
         url.includes('crystalmedia') ||
         url.startsWith('/');
};

const ServicesPage = () => {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState(null);
  const [logoUrl, setLogoUrl] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, servicesRes] = await Promise.all([
          fetch(`${API_URL}/api/content/settings`),
          fetch(`${API_URL}/api/services`)
        ]);

        const settingsData = await settingsRes.json();
        const servicesData = await servicesRes.json();

        setSettings(settingsData);
        setServices(servicesData || []);

        const photoLogo = settingsData.photo_logo_url || settingsData.logo_url || '';
        setLogoUrl(isValidLogoUrl(photoLogo) ? photoLogo : '');
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-[#fafafa]">
      <SEOHead
        title="Wedding Photography & Videography Services"
        description="Explore Crystal Media's luxury wedding services including cinematic wedding films, editorial wedding photography, Pakistani wedding highlights, Indian wedding coverage, and destination wedding packages across Toronto, Vancouver, Montreal, and the USA."
        url="/services"
        keywords={['Wedding Services Toronto', 'Wedding Packages', 'Destination Wedding Services']}
        jsonLd={breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'Services', path: '/services' }])}
      />
      {/* Shared Header */}
      <Header logoUrl={logoUrl} variant="light" />

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal><p className="text-[0.6rem] tracking-[0.3em] uppercase text-black/30 mb-4">What We Offer</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-presti text-4xl md:text-5xl lg:text-6xl text-black mb-6">Our Services</h1></Reveal>
          <Reveal delay={0.16}>
            <p className="text-black/50 text-lg max-w-2xl mx-auto leading-relaxed">
              From timeless photography to cinematic films, we offer a complete suite of services to capture every moment of your celebration.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Services Grid */}
      <section className="pb-24 px-6 lg:px-12">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <p className="text-black/30 animate-pulse">Loading services...</p>
            </div>
          ) : services.length > 0 ? (
            <Stagger className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const IconComponent = iconMap[service.icon] || Camera;
                return (
                  <StaggerItem key={service.id || index} y={36}>
                  <Link
                    key={service.id}
                    to={`/services/${service.slug}`}
                    className="group bg-white p-8 rounded-lg shadow-sm hover:shadow-lg transition-all duration-500 border border-black/5 hover:border-black/10"
                    data-testid={`service-card-${service.slug}`}
                  >
                    <div className="mb-6">
                      <div className="w-14 h-14 rounded-full bg-black/5 flex items-center justify-center group-hover:bg-black group-hover:text-white transition-all duration-300">
                        <IconComponent size={24} className="text-black/40 group-hover:text-white transition-colors" />
                      </div>
                    </div>
                    <h3 className="font-presti text-xl text-black mb-3 group-hover:text-black transition-colors">
                      {service.title}
                    </h3>
                    <p className="text-black/50 text-sm leading-relaxed mb-6">
                      {service.short_description}
                    </p>
                    <div className="flex items-center gap-2 text-xs tracking-[0.15em] uppercase text-black/40 group-hover:text-black transition-colors">
                      <span>Learn More</span>
                      <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                  </StaggerItem>
                );
              })}
            </Stagger>
          ) : (
            <div className="text-center py-20">
              <p className="text-black/30 text-sm tracking-widest uppercase">No services available</p>
              <p className="text-black/20 text-xs mt-2">Services will appear here once added in the admin panel</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-white border-t border-black/5">
        <Reveal className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="font-presti text-2xl md:text-3xl text-black mb-6">
            Ready to discuss your vision?
          </h2>
          <p className="text-black/50 mb-8">
            Let us know about your event and we will create a custom package tailored to your needs.
          </p>
          <Magnetic>
          <Link
            to="/inquire"
            data-testid="services-cta-inquire"
            className="inline-flex items-center gap-3 px-10 py-4 bg-black text-white text-xs tracking-[0.2em] uppercase hover:bg-black/80 transition-colors duration-300"
          >
            <span>Get in Touch</span>
            <ArrowRight size={14} />
          </Link>
          </Magnetic>
        </Reveal>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-black/5 bg-[#fafafa]">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-6">
          {logoUrl ? (
            <img src={logoUrl} alt="Crystal Media" className="h-6 w-auto opacity-50" />
          ) : (
            <span className="font-presti text-xs tracking-[0.2em] text-black/40">CRYSTAL MEDIA</span>
          )}
          <p className="text-[10px] tracking-[0.15em] uppercase text-black/30">
            © 2025 Crystal Media. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default ServicesPage;
